import { Variable } from "@/types";

export const abbreviateNumber = (value: number) => {
  const SI_SYMBOL = ["", "k", "M", "B", "T", "P", "E"];
  const tier = (Math.log10(Math.abs(value)) / 3) | 0;
  if (tier === 0) return value;
  const suffix = SI_SYMBOL[tier];
  const scale = Math.pow(10, tier * 3);
  const scaled = value / scale;
  return scaled.toFixed(1) + suffix;
};

export const reformatDate = (date: string | undefined) => {
  const dateObj = new Date(date || "");
  const day = dateObj.getDate();
  const month = dateObj.toLocaleString("default", { month: "short" });
  const year = dateObj.getFullYear();
  return `${day} ${month} ${year}`;
};

export const toVariableLabel = (variable: Variable, extended: boolean = false) => {
  if (variable.tag_type === "Demographics") {
    if (extended) {
      return `${variable.tag_group} | ${variable.tag_category} | ${variable.tag_name}`;
    } else {
      return `${variable.tag_group} | ${variable.tag_name}`;
    }
  } else {
    if (extended) {
      return `${variable.tag_type} | ${variable.tag_category} | ${variable.tag_name}`;
    } else {
      return `${variable.tag_type} | ${variable.tag_name}`;
    }
  }
};

export const toCategoryName = (name: string) => {
  return name.replace("All ", "");
};

export const toIconLabel = (name: string) => {
  return name
    .trim()
    .replace("All ", "")
    .replace(/&/g, "and")
    .replace(/ ?- ?/g, "_")
    .replace(/ /g, "_")
    .replace(/\//g, "_")
    .replace(/'/g, "")
    .replace(/,/g, "")
    .replace(/\+/g, "")
    .toLowerCase();
};

export const toChartLabel = (label: string) => {
  label = label.replace(/\(.+\)/g, "");
  if (label.includes(" - ")) {
    if (label.length < 15) {
      return label.replace(/\s/g, "");
    } else {
      let parts = label.split(" - ");
      return [parts[0] + " -", ...parts.slice(1)];
    }
  } else if (label.length > 11 && label.includes("-")) {
    let parts = label.split("-");
    return [parts[0] + " -", ...parts.slice(1)];
  } else if (label.includes("/")) {
    return label.split("/");
  } else if (label.length > 11) {
    return label.split(" ");
  } else {
    return label;
  }
};

export const combineWords = (arr: string | string[]) => {
  if (typeof arr === "string") {
    return arr;
  } else {
    return arr.reduce((acc: string[], word: string) => {
      if (word.length <= 4) {
        if (acc.length > 0 && acc[acc.length - 1].length <= 4) {
          acc[acc.length - 1] += " " + word;
        } else {
          acc.push(word);
        }
      } else {
        acc.push(word);
      }
      return acc;
    }, []);
  }
};

export const toRounded = (value: number) => {
  return Math.round(value);
};

export const toIndex = (value: number) => {
  return Number(value.toFixed(2));
};

export const toPercentage = (value: number, digits: number = 1) => {
  return `${(value * 100).toFixed(digits)}%`;
};

export const renderDateRange = (start: string, end: string | null): string => {
  if (!end) {
    return "Always On";
  }

  const parseDate = (date: string): Date =>
    new Date(date.split("/").reverse().join("-"));

  const formatDate = (date: Date): string =>
    date.toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });

  const startDateFormatted = formatDate(parseDate(start));
  const endDateFormatted = formatDate(parseDate(end));

  return `${startDateFormatted} - ${endDateFormatted}`;
};


export function formatSegmentNames(segments: string[], maxChars: number): string {
  if (segments.length === 0) return "";

  const out: string[] = [];
  let used = 0;

  for (let i = 0; i < segments.length; i++) {
    const remaining = segments.length - i;
    const needsMoreText = remaining > 0;

    // Estimate how many chars "+ N more" will take if needed
    const moreText = needsMoreText ? `+ ${remaining} more` : "";
    const moreLen = moreText.length > 0 ? (out.length > 0 ? 3 : 0) + moreText.length : 0; // + " | "

    const seg = segments[i];
    const sep = out.length > 0 ? " | " : "";
    const segLen = sep.length + seg.length;

    // Check if this segment + possible "more" fits
    if (used + segLen + moreLen <= maxChars) {
      out.push(seg);
      used += segLen;
    } else {
      out.push(moreText);
      break;
    }
  }

  return out.join(" | ");
}


export const formatDate = (dt: Date)=> {

  // Formats to: DD MMM YYYY
  const datePart = new Intl.DateTimeFormat('en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  }).format(dt);

  // Formats to: HH:MM
  const timePart = new Intl.DateTimeFormat('en-GB', {
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  }).format(dt);

  return `${datePart} - ${timePart}`;
}
